import { Link } from '@inertiajs/react';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/autoplay';

interface Genre {
    id: string;
    name: string;
    slug: string;
    translated_slug?: string;
    books_count?: number;
}

interface GenreSliderProps {
    genres: Genre[];
}

export function GenreSlider({ genres }: GenreSliderProps) {
    const { genreUrl } = useTranslatedRoutes();
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            // Show slider when scrolling up or at the top
            if (currentScrollY < lastScrollY || currentScrollY < 10) {
                setIsVisible(true);
            } 
            // Hide slider when scrolling down and not at the top
            else if (currentScrollY > lastScrollY && currentScrollY > 100) {
                setIsVisible(false);
            }
            
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    if (!genres || genres.length === 0) {
        return null;
    }

    // Duplicate genres to ensure smooth infinite loop
    const duplicatedGenres = [...genres, ...genres, ...genres];

    return (
        <div className={cn(
            "sticky top-20 z-30 border-b overflow-hidden transition-all duration-300",
            isVisible ? "translate-y-0 bg-background" : "-translate-y-full bg-transparent"
        )}>
            <div className="py-3">
                <Swiper
                    modules={[Autoplay]}
                    spaceBetween={12}
                    slidesPerView="auto"
                    loop={true}
                    autoplay={{
                        delay: 0,
                        disableOnInteraction: false,
                    }}
                    speed={8000}
                    className="genre-swiper"
                >
                    {duplicatedGenres.map((genre, index) => (
                        <SwiperSlide key={`${genre.id}-${index}`} className="!w-auto">
                            {genre.slug ? (
                                <Link
                                    href={genreUrl(genre.translated_slug || genre.slug)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                                >
                                    {genre.name}
                                </Link>
                            ) : (
                                <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-muted-foreground/50 whitespace-nowrap cursor-not-allowed">
                                    {genre.name}
                                </span>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            
            {/* Gradient overlays for smooth edges */}
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
        </div>
    );
}